import { GuestTokenUtils } from './GuestTokenUtils';
import { 
  getEnvironmentConfig, 
  API_ENDPOINTS, 
  CONSENT_CONFIG, 
  VALIDATION_MESSAGES, 
  MONITORING_CONFIG,
  LoadingState 
} from '../config/consentConfig';
import { 
  ConsentErrorHandler, 
  ApiError, 
  TokenError, 
  ValidationError,
  withRetry 
} from './errors';

// Import CBOR from cbor-js library
const CBOR = require('cbor-js');

// Common interfaces
export interface InterviewUpdateData {
  useCase:string;
  entityName:string;
  customObjectId?: string;
  requesterId: string;
  requesterType: string;
  customObjectFields?: string;
}

export interface ConsentSubmissionData {
  consentData:InterviewUpdateData;
  submissionType: string;
  decision: string;
}

export interface ConsentApiResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  alias?: string;
}

export interface TokenData {
  caseId: string;
  interviewId: string;
}

export interface UpdateCustomObjectResponse {
  success: boolean;
  customObjectId?: string;
  message?: string;
  error?: string;
}

// API Service class
export class ConsentApiService {
  private userType: 'guest' | 'authenticated';
  private userInfo?: UserInfo;
  private tokenData?: TokenData;

  constructor(userType: 'guest' | 'authenticated', userInfo?: UserInfo) {
    this.userType = userType;
    this.userInfo = userInfo;
  }

  // Initialize the service (validate tokens for guest, setup for auth)
  async initialize(): Promise<{ isValid: boolean; errorMessage?: string; tokenData?: TokenData }> {
    try {
      if (this.userType === 'guest') {
        return await this.initializeGuestAccess();
      } else {
        return await this.initializeAuthenticatedAccess();
      }
    } catch (error) {
      const context = this.userType === 'guest' ? 'Guest Token Validation' : 'Authenticated Access Initialization';
      const errorResult = ConsentErrorHandler.handleApiError(error, context);
      return {
        isValid: false,
        errorMessage: errorResult.error
      };
    }
  }

  private async initializeGuestAccess(): Promise<{ isValid: boolean; errorMessage?: string; tokenData?: TokenData }> {
    try {
      // Extract token from URL
      const token = GuestTokenUtils.extractTokenFromUrl();
      
      if (!token) {
        throw new TokenError(VALIDATION_MESSAGES.TOKEN_NOT_PROVIDED, 'missing');
      }

      // Validate token
      const validationResult = await GuestTokenUtils.parseAndValidateToken(token);
      
      if (!validationResult.isValid || validationResult.isExpired) {
        const tokenType = validationResult.isExpired ? 'expired' : 'invalid';
        
        
        throw new TokenError(
          validationResult.errorMessage || VALIDATION_MESSAGES.TOKEN_INVALID_OR_EXPIRED,
          tokenType
        );
      }

      // Store token data
      this.tokenData = {
        caseId: validationResult.caseId,
        interviewId: validationResult.interviewId
      };


      return {
        isValid: true,
        tokenData: this.tokenData
      };

    } catch (error) {
      const errorResult = ConsentErrorHandler.handleApiError(error, 'Guest Token Validation');
      return {
        isValid: false,
        errorMessage: errorResult.error
      };
    }
  }

  private async initializeAuthenticatedAccess(): Promise<{ isValid: boolean; errorMessage?: string }> {
    try {
      // For authenticated users, we assume they're already validated by the auth system
      
      return {
        isValid: true
      };
    } catch (error) {
      const errorResult = ConsentErrorHandler.handleApiError(error, 'Authenticated Access Initialization');
      return {
        isValid: false,
        errorMessage: errorResult.error
      };
    }
  }

  // Submit consent decision
  async submitConsent(data: ConsentSubmissionData): Promise<ConsentApiResponse> {
    try {
      // Log the consent decision (only in development)
      const envConfig = getEnvironmentConfig();
      if (envConfig.enableLogging) {
        console.log(`Consent submission:`, data);
      }

      // Call UpdateCustomObject API with retry mechanism
      const updateResult = await withRetry(async () => {
        return this.updateCustomObject(data);
      });
      
      if (!updateResult.success) {
        throw new ApiError(
          updateResult.error || VALIDATION_MESSAGES.CUSTOM_OBJECT_UPDATE_FAILED,
          undefined,
          'CUSTOM_OBJECT_UPDATE_FAILED',
          'Consent Submission'
        );
      }


      return {
        success: true,
        message: 'Consent submitted successfully'
      };

    } catch (error) {
      const errorResult = ConsentErrorHandler.handleApiError(error, 'Consent Submission');
      return {
        success: false,
        error: errorResult.error
      };
    }
  }

  // Update Custom Object API call using RPC v2 CBOR protocol
  private async updateCustomObject(data: ConsentSubmissionData): Promise<UpdateCustomObjectResponse> {
    try {
      // Use environment-based configuration
      const envConfig = getEnvironmentConfig();
      const updateUrl = `${envConfig.apiBaseUrl}${API_ENDPOINTS.UPDATE_CUSTOM_OBJECT}`;

      // Encode request to CBOR
      const cborData = CBOR.encode(data.consentData);

      if (envConfig.enableLogging) {
        console.log('Calling UpdateCustomObject API with RPC v2 CBOR protocol...');
      }

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/cbor',
        'Accept': 'application/cbor',
        'smithy-protocol': 'rpc-v2-cbor'
      };

      // Add appropriate authorization header based on user type
      if (this.userType === 'guest') {
        const token = GuestTokenUtils.extractTokenFromUrl();
        if (token) {
          headers['X-Amzn-Guest-Token'] = token;
        }
      } else {
        // For authenticated users, you might need to add Authorization header
        // This would typically come from the authentication context
        // headers['Authorization'] = 'Bearer ' + authToken;
      }

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers,
        body: cborData,
      });

      if (!response.ok) {
        // Use enhanced error handling
        const apiError = await ConsentErrorHandler.createApiErrorFromResponse(
          response, 
          'UpdateCustomObject API'
        );
        throw apiError;
      }

      // Decode CBOR response
      const responseBuffer = await response.arrayBuffer();
      const result = CBOR.decode(responseBuffer);
      
      if (envConfig.enableLogging) {
        console.log('UpdateCustomObject API call successful:', result);
      }

      return {
        success: true,
        customObjectId: result.customObjectId,
        message: 'Custom object updated successfully'
      };

    } catch (error) {
      if (error instanceof ApiError) {
        throw error; // Re-throw API errors to be handled by the retry mechanism
      }
      
      const errorResult = ConsentErrorHandler.handleApiError(error, 'UpdateCustomObject API');
      return {
        success: false,
        error: errorResult.error
      };
    }
  }


  // Get current token data (for guest users)
  getTokenData(): TokenData | undefined {
    return this.tokenData;
  }

  // Get user info (for authenticated users)
  getUserInfo(): UserInfo | undefined {
    return this.userInfo;
  }
}

// Factory function to create the appropriate service instance
export const createConsentApiService = (
  userType: 'guest' | 'authenticated',
  userInfo?: UserInfo
): ConsentApiService => {
  return new ConsentApiService(userType, userInfo);
};

export default ConsentApiService;
